<template>
    <ion-page>
        <ion-header>
            <ion-toolbar>
                <ion-buttons slot="end">
                    <ion-button @click="close()">
                        <ion-icon slot="icon-only" :icon="closeOutline"></ion-icon>
                    </ion-button>
                </ion-buttons>
                <ion-title>{{ title }}</ion-title>
            </ion-toolbar>
        </ion-header>
        <ion-content :fullscreen="true">
            <ion-list>
                <ion-item>
                    <ion-label class="ion-text-wrap">
                        <h2>Item Number</h2>
                        <p>
                            {{ line.item.item_number }} / {{ line.sku.sku }}
                            <ion-badge v-show="line.pick_b_stock" color="danger">B-Stock</ion-badge>
                            <ion-icon v-show="itemMatched" color="success" :icon="checkmarkCircle"/>
                        </p>
                    </ion-label>
                    <ion-button size="medium" color="primary" @click="openItemScanner()">
                        <ion-icon slot="icon-only" :icon="barcode"/>
                    </ion-button>
                    <ion-button expand="block" color="primary" size="medium" @click="openItemPicker()">
                        <ion-icon slot="icon-only" :icon="search"/>
                    </ion-button>
                </ion-item>
                <ion-item v-if="pickerErrors(line).length > 0">
                    <ion-label v-if="pickerErrors(line).length > 0" class="ion-text-wrap">
                        <h2><ion-text color="danger">Picker Errors</ion-text></h2>
                        <p v-for="error in pickerErrors(line)" :key="error.id">
                            {{ error.author.initials }} - {{ error.code.name }}
                        </p>
                    </ion-label>
                </ion-item>
                <ion-item v-if="checkerErrors(line).length > 0">
                    <ion-label v-if="checkerErrors(line).length > 0" class="ion-text-wrap">
                        <h2><ion-text color="danger">Checker Errors</ion-text></h2>
                        <p v-for="error in checkerErrors(line)" :key="error.id">
                            {{ error.author.initials }} - {{ error.code.name }}
                        </p>
                    </ion-label>
                </ion-item>
                <ion-item>
                    <ion-label class="ion-text-wrap">
                        <h2>Picking Instructions</h2>
                        <p>
                            {{ line.picking_instructions ?? 'No specific instructions' }}
                        </p>
                    </ion-label>
                </ion-item>
                <ion-item>
                    <ion-label class="ion-text-wrap">
                        <h2>Checking Instructions</h2>
                        <p>
                            {{ checking_instructions ?? 'No specific instructions' }}
                        </p>
                        <ion-text color="warning" v-if="likelySubstitute">{{ substituteInformation }}</ion-text>
                    </ion-label>
                    <ion-button size="medium" color="primary" @click="editInstructions('checking_instructions', 'Checking Instructions')">
                        <ion-icon slot="icon-only" :icon="pencilOutline"/>
                    </ion-button>
                </ion-item>
                <ion-item>
                    <ion-label class="ion-text-wrap">
                        <h2>Packing Instructions</h2>
                        <p>
                            {{ packing_instructions ?? 'No specific instructions' }}
                        </p>
                    </ion-label>
                    <ion-button size="medium" color="primary" @click="editInstructions('packing_instructions', 'Packing Instructions')">
                        <ion-icon slot="icon-only" :icon="pencilOutline"/>
                    </ion-button>
                </ion-item>
                <div v-if="line.errors && line.errors.length > 0">
                    <ion-item v-for="error in line.errors" :key="error.id">
                        <ion-label>
                            <h2><ion-text color="danger">{{ error.code.name }}</ion-text></h2>
                            <p v-if="error.reason_code_custom_message">{{ error.reason_code_custom_message }}</p>
                        </ion-label>
                    </ion-item>
                </div>
                <ion-item>
                    <ion-button size="medium" style="width:100%"
                                fill="outline"
                                color="dark"
                                class="ion-margin-top ion-margin-bottom"
                                @click="$refs.product.open()">Product Card
                    </ion-button>
                </ion-item>
                <ion-item>
                    <check-quantity class="ion-margin-top ion-margin-bottom" :line="line" key="1" v-model="qty_checked"/>
                </ion-item>
                <div v-if="shipment.is_list || line.is_child"></div>
                <div v-else>
                    <p class="ion-text-center" v-if="highestBoxNumber > 0">Last Box Number: {{ highestBoxNumber }}</p>
                    <p class="ion-text-center" v-if="shipment.is_pallets && highestPalletNumber > 0">Last Pallet Number: {{ highestPalletNumber }}</p>
                    <multi-box-toggle v-model="box_plan.multi" />
                    <new-box-toggle v-model="box_plan.create" v-if="!box_plan.multi"/>
                    <multi-box-plan v-if="box_plan.multi" v-model="box_plan" :unboxed="line.checking.qty_unboxed" :is-pallets="shipment.is_pallets" />
                    <single-box-plan v-if="!box_plan.multi && !box_plan.create" v-model="box_plan" :unboxed="line.checking.qty_unboxed" />
                    <append-box-plan v-if="box_plan.create && !box_plan.multi" v-model="box_plan" :unboxed="line.checking.qty_unboxed" />
                </div>
            </ion-list>
            <failed-pick-count-reason-alert
                ref="alert" header="Check count does not match"
                group="CHECK_ERRORS"
                @submitted="setAnomalyCode($event)"
            />
        </ion-content>
        <product-card-modal ref="product" :item="line.item" :sku="line.sku" />
        <ion-footer>
            <ion-button expand="block" fill="outline" size="large" @click="submit()">{{ saveLabel }}</ion-button>
        </ion-footer>
    </ion-page>
</template>

<script>
import {modalController} from '@ionic/vue';
import {
    IonContent, IonPage, IonLabel, IonItem, IonHeader, IonToolbar,
    IonTitle, IonButtons, IonIcon, IonList, IonFooter, IonButton,
    IonText, IonBadge
} from '@ionic/vue';

import EventBus from "@/EventBus";
import {closeOutline, pencilOutline, search, barcode, checkmarkCircle} from 'ionicons/icons';

import ItemSearchModalHelper from "@/services/ItemSearchModalHelper";
import {Capacitor} from '@capacitor/core';
import CheckQuantity from "@/views/modules/checking/pages/check/modals/CheckQuantity";
import FailedPickCountReasonAlert from "@/components/Alerts/FailedPickCountReasonAlert";
import ProductCardModal from "@/components/Modals/ProductCardModal";
import MultiBoxPlan from "@/views/modules/checking/pages/check/components/MultiBoxPlan";
import MultiBoxToggle from "@/views/modules/checking/pages/check/components/MultiBoxToggle";
import SingleBoxPlan from "@/views/modules/checking/pages/check/components/SingleBoxPlan";
import NewBoxToggle from "@/views/modules/checking/pages/check/components/NewBoxToggle";
import AppendBoxPlan from "@/views/modules/checking/pages/check/components/AppendBoxPlan";
import {alertController} from '@ionic/vue';
import _ from "lodash";

export default {
    props: ['line', 'shipment', 'current-shipment-split', 'highestBoxNumber', 'highestPalletNumber'],
    inject: ['$http', '$toast', '$validationAlert', '$scanner'],
    name: 'CheckQuantityModal',
    components: {
        AppendBoxPlan,
        NewBoxToggle,
        SingleBoxPlan,
        MultiBoxToggle,
        MultiBoxPlan,
        ProductCardModal,
        FailedPickCountReasonAlert,
        CheckQuantity,
        IonContent, IonPage, IonLabel, IonItem, IonHeader, IonToolbar,
        IonTitle, IonButtons, IonButton, IonIcon, IonList, IonFooter,
        IonText, IonBadge
    },
    computed: {
        likelySubstitute() {
            return (this.line.sku?.item?.id !== this.line?.item?.id);
        },
        substituteInformation() {
            return `Looks like original '${this.line.sku.item.item_number}' has been substituted for '${this.line.item.item_number}'`;
        },
        saveLabel() {
            let label = 'Save';
            if (this.currentShipmentSplit) {
                label += ' - ' + this.currentShipmentSplit.reference;
            }
            return label;
        },
        title() {
            let title = this.shipment.reference;
            if (this.currentShipmentSplit) {
                title += ' - ' + this.currentShipmentSplit.reference;
            }
            return title;
        },
        payload() {
            const plan = {...this.box_plan};
            if (plan.multi) {
                delete plan.number;
            } else {
                delete plan.length;
                delete plan.width;
                delete plan.height;
                delete plan.weight;
                delete plan.count;
            }

            if (plan.create) {
                delete plan.number;
            }

            return {
                item: this.scannedItem,
                split: this.currentShipmentSplit,
                box_plan: plan,
                reason: this.reason,
                comment: this.comment,
                checked: this.qty_checked,
                checking_instructions: this.checking_instructions,
                packing_instructions: this.packing_instructions
            };
        },
        itemMatched() {
            return this.scannedItem?.item_number === this.line.item?.item_number;
        }
    },
    mounted() {
        this.checking_instructions = this.line.checking_instructions;
        this.packing_instructions = this.line.packing_instructions;
    },
    methods: {
        checkerErrors(line) {
            return _.filter(line.errors, error => {
                return error.code.group.code === 'CHECK_ERRORS';
            });
        },
        pickerErrors(line) {
            return _.filter(line.errors, error => {
                return error.code.group.code === 'PICK_ERRORS';
            });
        },
        async editInstructions(type, label) {
            const alert = await alertController.create({
                header: 'Edit ' + label,
                inputs: [{
                    type: 'textarea',
                    name: 'instructions',
                    value: this[type],
                    placeholder: label,
                }],
                buttons: [
                    {
                        text: 'Cancel',
                        role: 'cancel'
                    }, {
                        text: 'OK',
                        handler: data => {
                            this[type] = data.instructions;
                        }
                    }
                ],
            });
            return alert.present();
        },
        submit() {
            // undo the setting of a reason if we have matched the quantity
            // required ...
            if (this.reason && this.qty_checked == this.line.qty_required) {
                this.reason = {};
                this.comment = '';
            }

            if (this.qty_checked < 1 || this.qty_checked != this.line.qty_required) {
                this.$refs['alert'].present();
                return;
            }

            this.save();
        },
        setAnomalyCode(event) {
            this.reason = event.reason;
            this.comment = event.comment;
            this.save();
        },
        openItemScanner() {
            if (Capacitor.platform === 'web') {
                this.scannedItem = this.line.item;
                return;
            }
            this.$scanner.search().then(result => {
                this.scannedItem = result;
            }).catch(error => {
                this.$toast.danger(error);
            });
        },
        openItemPicker() {
            ItemSearchModalHelper.open().then(item => {
                this.scannedItem = item.data;
            });
        },
        close() {
            modalController.dismiss();
        },
        /**
         * Update the shipment line. By the time we call this
         * we've already sought the reason and comment if
         * required. They'll be passed in ...
         *
         */
        save() {
            EventBus().emitter.emit('loading', true);
            this.$http.put(`/api/checks/lines/${this.line.id}`, this.payload, {
                params: {
                    include: 'checkers,lines,lines.item,lines.sku,lines.location,lines.item.mpns,lines.item.skus,lines.item.skus.item,lines.checking,splits'
                }
            }).then(() => {
                this.$toast.success("Box plan updated")
                modalController.dismiss(true);
            }).catch(error => {
                this.$validationAlert(error);
            }).finally(() => {
                EventBus().emitter.emit('loading', false);
            });
        },
    },
    data() {
        return {
            create_box: false,
            box_plan: {
                create: false,
                multi: true,
                width: this.line.item.case_width_cm,
                height: this.line.item.case_height_cm,
                length: this.line.item.case_length_cm,
                weight: this.line.item.case_weight_kg,
                count: 0, // for creating new multiboxes
                per_box_quantity: 0,
                item_quantity: 0,
                number: 0 // for assigning to an existing box
            },
            comment: null,
            reason: null,
            scannedItem: {},
            qty_checked: this.line.qty_checked,
            packing_instructions: null,
            checking_instructions: null,
        }
    },
    setup() {
        return {
            checkmarkCircle,
            barcode,
            search,
            pencilOutline,
            closeOutline
        };
    }
}
</script>
<style>
ion-footer {
    background-color: var(--ion-background-color, #fff);
}
</style>
